import { useTranslation } from "@sagot/i18n";
import type { ReactNode } from "react";
import { formatBytes, formatDate, formatNumber } from "../shared/format";
import type { SourceView } from "../shared/types";

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 px-5 py-2.5">
      <dt className="shrink-0 text-fg-subtle">{term}</dt>
      <dd className="min-w-0 text-right font-semibold">{children}</dd>
    </div>
  );
}

function languageName(code: string | null, language: string): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames([language === "fil" ? "fil" : "en"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Metadata `dl` (left column). Only fields the API exposes; see report for missing ones. */
export function DetailsCard({ source }: { source: SourceView }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const k = "deployKnowledge.sourceDetail.details";
  const typeLabel = source.file_type ? source.file_type.toUpperCase() : t(`deployKnowledge.sourceDetail.types.${source.type}`, { defaultValue: source.type });
  const language = languageName(source.language, lang);
  const validity = source.effective_from ?? source.valid_until;
  return (
    <section className="card" aria-labelledby="source-details-title">
      <div className="card-header">
        <h2 id="source-details-title" className="card-title">
          {t(`${k}.title`)}
        </h2>
        <span className="font-mono text-[11px] text-fg-subtle">{source.public_id}</span>
      </div>
      <dl className="divide-y divide-line text-sm">
        <Row term={t(`${k}.type`)}>{typeLabel}</Row>
        {source.url ? (
          <Row term={t(`${k}.url`)}>
            <span className="block truncate font-mono text-[11px]">{source.url}</span>
          </Row>
        ) : null}
        {source.page_count !== null ? <Row term={t(`${k}.pages`)}>{formatNumber(source.page_count, lang)}</Row> : null}
        {source.size_bytes !== null ? <Row term={t(`${k}.size`)}>{formatBytes(source.size_bytes)}</Row> : null}
        <Row term={t(`${k}.version`)}>{t(`${k}.versionValue`, { version: source.version, date: formatDate(source.updated_at, lang) })}</Row>
        <Row term={t(`${k}.language`)}>{language ?? <span className="font-normal text-fg-subtle">{t(`${k}.languageUnknown`)}</span>}</Row>
        {source.last_synced_at ? <Row term={t(`${k}.lastSynced`)}>{formatDate(source.last_synced_at, lang, true)}</Row> : null}
        {validity ? (
          <Row term={t(`${k}.validity`)}>{t(`${k}.validityRange`, { from: formatDate(source.effective_from, lang), until: formatDate(source.valid_until, lang) })}</Row>
        ) : null}
        <Row term={t(`${k}.chunks`)}>
          {formatNumber(source.chunk_count, lang)}{" "}
          {source.excluded_count > 0 ? <span className="font-normal text-fg-subtle">{t(`${k}.excluded`, { count: source.excluded_count })}</span> : null}
        </Row>
        {source.warnings.length > 0 ? (
          <Row term={t(`${k}.warnings`)}>
            <ul className="space-y-1 text-xs font-normal text-fg-muted">
              {source.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Row>
        ) : null}
      </dl>
    </section>
  );
}
